/**
 * Manages keyboard and mouse input for the game
 */
export class InputManager {
  // Key states
  private keys: Map<string, boolean> = new Map();
  
  // Mouse position
  private mouseX: number = 0;
  private mouseY: number = 0;
  
  // Mouse movement
  private mouseDeltaX: number = 0;
  private mouseDeltaY: number = 0;
  
  // Mouse buttons
  private mouseButtons: Map<number, boolean> = new Map();
  
  // Pointer lock status
  private pointerLocked: boolean = false;
  
  // Callbacks
  private keyDownCallbacks: Array<(key: string) => void> = [];
  private keyUpCallbacks: Array<(key: string) => void> = [];
  private mouseDownCallbacks: Array<(button: number, x: number, y: number) => void> = [];
  private mouseUpCallbacks: Array<(button: number, x: number, y: number) => void> = [];
  private mouseMoveCallbacks: Array<(x: number, y: number, deltaX: number, deltaY: number) => void> = [];
  private wheelCallbacks: Array<(delta: number) => void> = [];
  private pointerLockChangeCallbacks: Array<(locked: boolean) => void> = [];
  
  // Target element for pointer lock
  private targetElement: HTMLElement | null = null;
  
  /**
   * Creates a new InputManager
   */
  constructor() {
    // Add event listeners
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('wheel', this.handleWheel);
    document.addEventListener('pointerlockchange', this.handlePointerLockChange);
    document.addEventListener('pointerlockerror', this.handlePointerLockError);
    
    console.log("InputManager initialized");
  }
  
  /**
   * Handles key down events
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    this.keys.set(event.key.toLowerCase(), true);
    
    // Call callbacks
    for (const callback of this.keyDownCallbacks) {
      callback(event.key.toLowerCase());
    }
  };
  
  /**
   * Handles key up events
   */
  private handleKeyUp = (event: KeyboardEvent): void => {
    this.keys.set(event.key.toLowerCase(), false);
    
    // Call callbacks
    for (const callback of this.keyUpCallbacks) {
      callback(event.key.toLowerCase());
    }
  };
  
  /**
   * Handles mouse down events
   */
  private handleMouseDown = (event: MouseEvent): void => {
    this.mouseButtons.set(event.button, true);
    
    // Call callbacks
    for (const callback of this.mouseDownCallbacks) {
      callback(event.button, event.clientX, event.clientY);
    }
  };
  
  /**
   * Handles mouse up events
   */
  private handleMouseUp = (event: MouseEvent): void => {
    this.mouseButtons.set(event.button, false);
    
    // Call callbacks
    for (const callback of this.mouseUpCallbacks) {
      callback(event.button, event.clientX, event.clientY);
    }
  };
  
  /**
   * Handles mouse move events
   */
  private handleMouseMove = (event: MouseEvent): void => {
    // Update mouse position
    const oldX = this.mouseX;
    const oldY = this.mouseY;
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
    
    // Calculate delta if pointer is locked
    if (this.pointerLocked) {
      this.mouseDeltaX = event.movementX;
      this.mouseDeltaY = event.movementY;
    } else {
      this.mouseDeltaX = this.mouseX - oldX;
      this.mouseDeltaY = this.mouseY - oldY;
    }
    
    // Call callbacks
    for (const callback of this.mouseMoveCallbacks) {
      callback(this.mouseX, this.mouseY, this.mouseDeltaX, this.mouseDeltaY);
    }
  };
  
  /**
   * Handles wheel events
   */
  private handleWheel = (event: WheelEvent): void => {
    // Call callbacks
    for (const callback of this.wheelCallbacks) {
      callback(event.deltaY);
    }
  };
  
  /**
   * Handles pointer lock change events
   */
  private handlePointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement !== null;
    
    console.log(`Pointer lock ${this.pointerLocked ? 'acquired' : 'released'}`);
    
    // Call callbacks
    for (const callback of this.pointerLockChangeCallbacks) {
      callback(this.pointerLocked);
    }
  };
  
  /**
   * Handles pointer lock error events
   */
  private handlePointerLockError = (event: Event): void => {
    console.error("Pointer lock error:", event);
    this.pointerLocked = false;
    
    // Call callbacks with locked = false
    for (const callback of this.pointerLockChangeCallbacks) {
      callback(false);
    }
  };
  
  /**
   * Checks if a key is pressed
   * @param key The key to check
   * @returns True if the key is pressed, false otherwise
   */
  public isKeyPressed(key: string): boolean {
    return this.keys.get(key.toLowerCase()) === true;
  }
  
  /**
   * Checks if a mouse button is pressed
   * @param button The button to check
   * @returns True if the button is pressed, false otherwise
   */
  public isMouseButtonPressed(button: number): boolean {
    return this.mouseButtons.get(button) === true;
  }
  
  /**
   * Gets the current mouse position
   * @returns The current mouse position as [x, y]
   */
  public getMousePosition(): [number, number] {
    return [this.mouseX, this.mouseY];
  }
  
  /**
   * Gets the current mouse movement
   * @returns The current mouse movement as [deltaX, deltaY]
   */
  public getMouseMovement(): [number, number] {
    return [this.mouseDeltaX, this.mouseDeltaY];
  }
  
  /**
   * Requests pointer lock on the specified element
   * @param element The element to request pointer lock on (defaults to document.body)
   */
  public requestPointerLock(element: HTMLElement = document.body): void {
    try {
      this.targetElement = element;
      
      // Request pointer lock
      element.requestPointerLock();
      console.log("Pointer lock requested");
    } catch (error) {
      console.error("Error requesting pointer lock:", error);
    }
  }
  
  /**
   * Exits pointer lock
   */
  public exitPointerLock(): void {
    if (document.exitPointerLock) {
      document.exitPointerLock();
    }
  }
  
  /**
   * Checks if pointer is locked
   * @returns True if pointer is locked, false otherwise
   */
  public isPointerLock(): boolean {
    return this.pointerLocked;
  }
  
  /**
   * Adds a callback for key down events
   * @param callback The callback to add
   */
  public addKeyDownCallback(callback: (key: string) => void): void {
    this.keyDownCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for key down events
   * @param callback The callback to remove
   */
  public removeKeyDownCallback(callback: (key: string) => void): void {
    const index = this.keyDownCallbacks.indexOf(callback);
    if (index !== -1) {
      this.keyDownCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for key up events
   * @param callback The callback to add
   */
  public addKeyUpCallback(callback: (key: string) => void): void {
    this.keyUpCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for key up events
   * @param callback The callback to remove
   */
  public removeKeyUpCallback(callback: (key: string) => void): void {
    const index = this.keyUpCallbacks.indexOf(callback);
    if (index !== -1) {
      this.keyUpCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for mouse down events
   * @param callback The callback to add
   */
  public addMouseDownCallback(callback: (button: number, x: number, y: number) => void): void {
    this.mouseDownCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for mouse down events
   * @param callback The callback to remove
   */
  public removeMouseDownCallback(callback: (button: number, x: number, y: number) => void): void {
    const index = this.mouseDownCallbacks.indexOf(callback);
    if (index !== -1) {
      this.mouseDownCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for mouse up events
   * @param callback The callback to add
   */
  public addMouseUpCallback(callback: (button: number, x: number, y: number) => void): void {
    this.mouseUpCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for mouse up events
   * @param callback The callback to remove
   */
  public removeMouseUpCallback(callback: (button: number, x: number, y: number) => void): void {
    const index = this.mouseUpCallbacks.indexOf(callback);
    if (index !== -1) {
      this.mouseUpCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for mouse move events
   * @param callback The callback to add
   */
  public addMouseMoveCallback(callback: (x: number, y: number, deltaX: number, deltaY: number) => void): void {
    this.mouseMoveCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for mouse move events
   * @param callback The callback to remove
   */
  public removeMouseMoveCallback(callback: (x: number, y: number, deltaX: number, deltaY: number) => void): void {
    const index = this.mouseMoveCallbacks.indexOf(callback);
    if (index !== -1) {
      this.mouseMoveCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for wheel events
   * @param callback The callback to add
   */
  public addWheelCallback(callback: (delta: number) => void): void {
    this.wheelCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for wheel events
   * @param callback The callback to remove
   */
  public removeWheelCallback(callback: (delta: number) => void): void {
    const index = this.wheelCallbacks.indexOf(callback);
    if (index !== -1) {
      this.wheelCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Adds a callback for pointer lock change events
   * @param callback The callback to add
   */
  public addPointerLockChangeCallback(callback: (locked: boolean) => void): void {
    this.pointerLockChangeCallbacks.push(callback);
  }
  
  /**
   * Removes a callback for pointer lock change events
   * @param callback The callback to remove
   */
  public removePointerLockChangeCallback(callback: (locked: boolean) => void): void {
    const index = this.pointerLockChangeCallbacks.indexOf(callback);
    if (index !== -1) {
      this.pointerLockChangeCallbacks.splice(index, 1);
    }
  }
  
  /**
   * Direct method to register for mouse down events
   * @param callback The callback to call when a mouse button is pressed
   */
  public onMouseDown(callback: (button: number, x: number, y: number) => void): void {
    this.addMouseDownCallback(callback);
  }
  
  /**
   * Direct method to register for mouse up events
   * @param callback The callback to call when a mouse button is released
   */
  public onMouseUp(callback: (button: number, x: number, y: number) => void): void {
    this.addMouseUpCallback(callback);
  }
  
  /**
   * Direct method to register for mouse move events
   * @param callback The callback to call when the mouse moves
   */
  public onMouseMove(callback: (x: number, y: number, deltaX: number, deltaY: number) => void): void {
    this.addMouseMoveCallback(callback);
  }
  
  /**
   * Direct method to register for key down events
   * @param callback The callback to call when a key is pressed
   */
  public onKeyDown(callback: (key: string) => void): void {
    this.addKeyDownCallback(callback);
  }
  
  /**
   * Direct method to register for key up events
   * @param callback The callback to call when a key is released
   */
  public onKeyUp(callback: (key: string) => void): void {
    this.addKeyUpCallback(callback);
  }
  
  /**
   * Cleans up resources when the input manager is no longer needed
   */
  public dispose(): void {
    // Remove event listeners
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('wheel', this.handleWheel);
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange);
    document.removeEventListener('pointerlockerror', this.handlePointerLockError);
    
    // Clear callbacks
    this.keyDownCallbacks = [];
    this.keyUpCallbacks = [];
    this.mouseDownCallbacks = [];
    this.mouseUpCallbacks = [];
    this.mouseMoveCallbacks = [];
    this.wheelCallbacks = [];
    this.pointerLockChangeCallbacks = [];
  }
} 